var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useDropdownMenuContext } from '../dropdown-menu-context';
import { CheckIcon } from '../../icon';
import { MenuItem } from '../../menu';
import { styled } from '../../styled';
import clsx from '../../utils/clsx';
import { dropdownMenuListItemClasses, dropdownMenuListItemComponentName, getDropdownMenuListItemUtilityClass, } from './dropdown-menu-list-item-classes';
import { useMergedClasses } from '../../styles/classes';
var Root = styled(MenuItem, { name: dropdownMenuListItemComponentName, slot: 'Root' })(function (_a) {
    var _b, _c, _d, _e;
    var theme = _a.theme, ownerState = _a.ownerState;
    return (__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, theme.typography.caption), { fontWeight: theme.typography.fontWeightRegular, color: theme.tokens.color_fg_link_subtle, height: 'auto', display: 'flex', alignItems: 'center', width: '100%', padding: theme.spacing(0.5, 1), borderRadius: 6, transition: theme.transitions.create(['color', 'background-color', 'box-shadow'], {
            duration: theme.transitions.duration.shortest,
        }), '&:active,&:hover': {
            color: theme.tokens.color_fg_link_subtle_hover,
            backgroundColor: theme.tokens.color_bg_interactive_hover,
            boxShadow: 'none',
        }, '&:focus:not(:active)': __assign({ backgroundColor: 'transparent' }, theme.mixins.focusRing()), '&.Mui-selected': {
            color: theme.tokens.color_fg_selected,
            backgroundColor: 'transparent',
            '& $startIcon': {
                color: 'currentColor',
            },
            '&:active,&:hover': {
                backgroundColor: theme.tokens.color_bg_interactive_hover,
                color: theme.tokens.color_fg_selected,
                '& $startIcon': {
                    color: 'currentColor',
                },
            },
            '&.Mui-disabled': {
                color: theme.tokens.color_fg_disabled,
                backgroundColor: 'transparent',
            },
        }, '&.Mui-disabled': {
            opacity: 1,
            color: theme.tokens.color_fg_disabled,
        } }), (ownerState.size === 'large' && {
        padding: theme.spacing(0.75, 1.5),
    })), (ownerState.color === 'danger' && (_b = {
            color: theme.tokens.color_fg_link_danger
        },
        _b["& .".concat(dropdownMenuListItemClasses.startIcon, ", & .").concat(dropdownMenuListItemClasses.endIcon)] = {
            color: 'currentColor',
        },
        _b['&:active,&:hover'] = {
            color: theme.tokens.color_fg_link_danger_hover,
            backgroundColor: theme.tokens.color_bg_link_danger_hover,
        },
        _b['&:focus:not(:active)'] = __assign({ backgroundColor: 'transparent' }, theme.mixins.focusRing({ color: theme.tokens.color_border_focus_state_danger })),
        _b['&:$disabled'] = {},
        _b))), (ownerState.selected && (_c = {},
        _c["& .".concat(dropdownMenuListItemClasses.selected)] = {},
        _c))), (ownerState.disabled && (_d = {},
        _d["& .".concat(dropdownMenuListItemClasses.disabled)] = {},
        _d))), (_e = {}, _e["& .".concat(dropdownMenuListItemClasses.icon)] = {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: theme.typography.pxToRem(16),
        '& > svg': {
            height: '1em',
            width: '1em',
        },
    }, _e)));
});
var StartIcon = styled('div', { name: dropdownMenuListItemComponentName, slot: 'StartIcon' })(function (_a) {
    var theme = _a.theme;
    return ({
        marginRight: theme.spacing(1),
        color: theme.tokens.color_fg_default,
    });
});
var EndIcon = styled('div', { name: dropdownMenuListItemComponentName, slot: 'EndIcon' })(function (_a) {
    var theme = _a.theme;
    return ({
        marginLeft: theme.spacing(1),
        color: theme.tokens.color_fg_decorative,
    });
});
var Content = styled('div', { name: dropdownMenuListItemComponentName, slot: 'Content' })({
    flex: 1,
    overflow: 'hidden',
});
var Title = styled('div', { name: dropdownMenuListItemComponentName, slot: 'Title' })(function (_a) {
    var ownerState = _a.ownerState, theme = _a.theme;
    return ({
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: ownerState.isDetailed ? theme.typography.fontWeightMedium : undefined,
    });
});
var Description = styled('div', { name: dropdownMenuListItemComponentName, slot: 'Description' })(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.tokens.color_fg_default,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    });
});
var SelectedIcon = styled('div', { name: dropdownMenuListItemComponentName, slot: 'SelectedIcon' })(function (_a) {
    var theme = _a.theme;
    return ({
        marginLeft: theme.spacing(1),
        color: theme.tokens.color_fg_selected,
    });
});
export var DropdownMenuListItem = React.forwardRef(function (props, ref) {
    var _a = props.component, component = _a === void 0 ? 'button' : _a, title = props.title, children = props.children, description = props.description, startIcon = props.startIcon, endIcon = props.endIcon, className = props.className, selected = props.selected, color = props.color, disabled = props.disabled, propClasses = props.classes, rootProps = __rest(props, ["component", "title", "children", "description", "startIcon", "endIcon", "className", "selected", "color", "disabled", "classes"]);
    var classes = useMergedClasses(dropdownMenuListItemClasses, getDropdownMenuListItemUtilityClass, propClasses);
    var _b = useDropdownMenuContext(), size = _b.size, onSelectItem = _b.onSelectItem;
    var isDetailed = !!description;
    var titleContent = title || children;
    var ComponentProp = component;
    if (ComponentProp === 'button' && rootProps.href) {
        ComponentProp = 'a';
    }
    var buttonProps = {};
    if (ComponentProp === 'button') {
        buttonProps.type = 'button';
        buttonProps.disabled = disabled;
    }
    else {
        if (ComponentProp !== 'a' || !rootProps.href) {
            buttonProps.role = 'button';
        }
        buttonProps['aria-disabled'] = disabled;
    }
    var handleItemClick = function (event) {
        onSelectItem();
        if (rootProps.onClick) {
            rootProps.onClick(event);
        }
    };
    var ownerState = {
        size: size,
        isDetailed: isDetailed,
        selected: selected,
        color: color,
        disabled: disabled,
    };
    return (React.createElement(Root, __assign({ selected: selected, onClick: handleItemClick, ownerState: ownerState, className: clsx(classes.root, isDetailed && classes.detailed, className), component: ComponentProp, disabled: disabled, ref: ref, title: typeof titleContent === 'string' ? titleContent : undefined }, rootProps, buttonProps),
        !!startIcon && React.createElement(StartIcon, { className: clsx(classes.icon, classes.startIcon) }, startIcon),
        React.createElement(Content, { className: classes.content },
            React.createElement(Title, { ownerState: { isDetailed: isDetailed }, className: classes.title }, titleContent),
            !!description && React.createElement(Description, { className: classes.description }, description)),
        !!endIcon && React.createElement(EndIcon, { className: clsx(classes.icon, classes.endIcon) }, endIcon),
        !!selected && (React.createElement(SelectedIcon, { className: clsx(classes.icon, classes.selectedIcon) },
            React.createElement(CheckIcon, null)))));
});
